import AutoStories from '@mui/icons-material/AutoStories'
import Code from '@mui/icons-material/Code'
import WorkspacePremium from '@mui/icons-material/WorkspacePremium'
import Check from '@mui/icons-material/Check'
import { isAchievementNode } from './drSecDimConfig'
import * as Styled from './DrSecDimStyles'

interface DrSecDimPathPreviewProps {
	nodes: PathNode[]
	selectedNodeIds: Set<string>
	onToggleNode: (id: string) => void
	disabled: boolean
}

const KIND_ICON = {
	learn: AutoStories,
	play: Code,
	achievement: WorkspacePremium,
} as const

const STATUS_TEXT: Record<PathNodeStatus, string> = {
	completed: 'Completed',
	current: 'Current step',
	upcoming: 'Upcoming',
}

function DrSecDimPathPreview({
	nodes,
	selectedNodeIds,
	onToggleNode,
	disabled,
}: DrSecDimPathPreviewProps) {
	const lastIndex = nodes.length - 1

	return (
		<Styled.PathRow
			role='list'
			aria-label='Learning pathway'
			data-tour='dr-secdim-path-preview'
		>
			{nodes.map((node, index) => {
				const Icon = KIND_ICON[node.kind]
				const isCurrent = node.status === 'current'
				const isCompleted = node.status === 'completed'
				const isAchievement = isAchievementNode(node)
				const prevCompleted = index > 0 && nodes[index - 1].status === 'completed'
				// Only the current node and upcoming ones can be swapped out; a
				// completed node or a future achievement node has nothing to pick.
				const isSelectable = !isCompleted && !isAchievement
				const isPicked = isSelectable && selectedNodeIds.has(node.id)

				const leftColor: Styled.ConnectorColor =
					index === 0 ? 'hidden' : prevCompleted ? 'green' : 'grey'
				const rightColor: Styled.ConnectorColor =
					index === lastIndex ? 'dotted' : isCompleted ? 'green' : 'grey'

				const nodeVariant: Styled.TimelineNodeVariant = isAchievement
					? 'achievement'
					: node.status

				const statusText = isAchievement
					? `Achievement: ${node.title}, ${STATUS_TEXT[node.status].toLowerCase()}`
					: STATUS_TEXT[node.status]

				return (
					<Styled.TimelineItem
						key={node.id}
						role='listitem'
						aria-current={isCurrent ? 'step' : undefined}
					>
						<Styled.RailRow>
							<Styled.ConnectorLeft color={leftColor} aria-hidden='true' />
							<Styled.ConnectorRight color={rightColor} aria-hidden='true' />
							{isSelectable ? (
								<Styled.NodeBadgeButton
									type='button'
									disabled={disabled}
									aria-pressed={isPicked}
									aria-label={
										isPicked
											? `Deselect "${node.title}" for regeneration`
											: `Select "${node.title}" for regeneration`
									}
									onClick={() => onToggleNode(node.id)}
								>
									<Styled.TimelineNode
										nodeVariant={nodeVariant}
										picked={isPicked}
										aria-hidden='true'
									>
										<Icon />
									</Styled.TimelineNode>
									{isPicked && (
										<Styled.PickIndicator aria-hidden='true'>
											<Check />
										</Styled.PickIndicator>
									)}
								</Styled.NodeBadgeButton>
							) : (
								<Styled.NodeBadgeAnchor>
									<Styled.TimelineNode nodeVariant={nodeVariant} aria-hidden='true'>
										{isAchievement ? <WorkspacePremium /> : <Check />}
									</Styled.TimelineNode>
								</Styled.NodeBadgeAnchor>
							)}
						</Styled.RailRow>
						<Styled.TimelineBody>
							{isAchievement ? (
								<>
									<Styled.AchievementTitle>{node.title}</Styled.AchievementTitle>
									{node.achievement?.note && (
										<Styled.NodeMetaText>{node.achievement.note}</Styled.NodeMetaText>
									)}
								</>
							) : (
								<>
									<Styled.NodeTitle
										sx={
											isCurrent ? { color: 'primary.main', fontWeight: 600 } : undefined
										}
									>
										{node.title}
									</Styled.NodeTitle>
									{node.points !== undefined && (
										<Styled.NodeMetaText>{node.points} pts</Styled.NodeMetaText>
									)}
								</>
							)}
						</Styled.TimelineBody>
						<Styled.VisuallyHidden>{statusText}</Styled.VisuallyHidden>
					</Styled.TimelineItem>
				)
			})}
			<Styled.PathTail aria-hidden='true'>
				<Styled.PathTailLine />
			</Styled.PathTail>
		</Styled.PathRow>
	)
}

export default DrSecDimPathPreview
